/**
 * pm-pdp.js - product page behaviour that Dawn does not provide.
 *
 * TWO JOBS, BOTH ADDITIVE
 * -----------------------
 * 1. SIZE GATE
 *    main-product.liquid renders no default size and ships the submit button
 *    disabled reading "Select a size". This enables it and swaps the label to
 *    "Add to bag" as soon as a size is chosen.
 *
 *    It deliberately does NOT decide availability. That stays with Dawn's
 *    product-info.js, which re-renders the button from the server after every
 *    option change. This script only refuses to unlock a button whose size is
 *    still unchosen, and steps aside once a size IS chosen, so the two never
 *    fight over the label.
 *
 * 2. MOBILE STICKY BAR
 *    Shows once the real add-to-bag button has scrolled out of view. It is a
 *    mirror: it scrolls to and submits the real button, so there is exactly
 *    one checkout path and one source of the variant id.
 *
 * NO-OP SAFETY
 * ------------
 * Every entry point bails out if the markup is absent, so a partial render or
 * the section used elsewhere cannot throw. Reduced motion is handled in CSS.
 */

(function () {
  'use strict';

  var LABEL_SELECT = 'Select a size';
  var LABEL_ADD = 'Add to bag';

  function productInfo() {
    return document.querySelector('product-info');
  }

  function submitButton(root) {
    return root ? root.querySelector('.product-form__submit') : null;
  }

  function sizeFieldset(root) {
    return root ? root.querySelector('[data-pm-size-option]') : null;
  }

  function hasChosenSize(root) {
    var fs = sizeFieldset(root);
    if (!fs) return true; // no size row: nothing to gate
    return !!fs.querySelector('input[type="radio"]:checked');
  }

  /**
   * Reflect the size state onto the real button and the sticky mirror.
   * Never overrides a button Dawn has disabled for availability: if a size IS
   * chosen and the button is still disabled, the variant is unavailable and
   * Dawn's own label is the correct thing to show.
   *
   * The ?variant= EXCEPTION matters here too. On a URL that already names a
   * variant, Liquid renders the size filled in and ships the button enabled;
   * re-gating it on load would show "Select a size" over a visibly selected
   * size, with a dead button. The size fieldset is marked data-pm-size-gated
   * by Liquid only when it actually suppressed the default selection, so
   * "is this page gated" is answered by the markup rather than guessed here.
   * The two must be changed together.
   */
  function syncButtons(root) {
    var btn = submitButton(root);
    if (!btn) return;

    var span = btn.querySelector('span');
    var stickyBtn = document.querySelector('[data-pm-sticky-btn]');
    var gated = !!root.querySelector('[data-pm-size-gated]');

    if (!gated || hasChosenSize(root)) {
      // Dawn owns the label from here. Only fill it in if it is still ours.
      if (span && /select a size/i.test(span.textContent || '')) {
        span.textContent = LABEL_ADD;
      }
      if (span && !btn.hasAttribute('disabled')) {
        btn.removeAttribute('aria-disabled');
      }
      if (stickyBtn) {
        var unavailable = btn.hasAttribute('disabled');
        stickyBtn.disabled = unavailable;
        stickyBtn.textContent = unavailable && span
          ? (span.textContent || '').trim()
          : LABEL_ADD;
      }
    } else {
      btn.setAttribute('disabled', 'disabled');
      if (span) span.textContent = LABEL_SELECT;
      if (stickyBtn) {
        stickyBtn.disabled = true;
        stickyBtn.textContent = LABEL_SELECT;
      }
    }
  }

  function initSizeGate() {
    var root = productInfo();
    if (!root) return;

    syncButtons(root);

    // change bubbles, so one delegated listener covers every size input now
    // and any that Dawn replaces after a variant fetch.
    root.addEventListener('change', function (event) {
      var t = event.target;
      if (t && t.matches && t.matches('[data-pm-size-option] input')) {
        // Let Dawn's own handler run first, then reconcile.
        requestAnimationFrame(function () {
          syncButtons(productInfo() || root);
        });
      }
    });

    // Dawn replaces the buy box wholesale after a variant change, which
    // discards the enabled state. Re-apply once the new markup lands.
    document.addEventListener('product-info:loaded', function () {
      syncButtons(productInfo());
    });

    // Last-resort reconciliation: if the disabled attribute is removed
    // wholesale while a gated page still has no size chosen, restore the
    // gate. Observes the one attribute that matters.
    var btn0 = submitButton(root);
    if (btn0 && 'MutationObserver' in window) {
      new MutationObserver(function () {
        var r = productInfo() || root;
        var b = submitButton(r);
        if (b && r.querySelector('[data-pm-size-gated]') && !hasChosenSize(r) && !b.hasAttribute('disabled')) {
          syncButtons(r);
        }
      }).observe(btn0, { attributes: true, attributeFilter: ['disabled'] });
    }
  }

  function initStickyBar() {
    var bar = document.querySelector('[data-pm-sticky-bar]');
    if (!bar) return;

    var root = productInfo();
    if (!root) return;
    var target = submitButton(root);
    if (!target) return;

    // Must match the CSS breakpoint that hides the bar (.pm-sticky-bar is
    // display:none from 990px up). If these drift apart the bar is shown on
    // desktop by JS while CSS has already hidden it, and it never appears on
    // tablet widths between the two thresholds.
    var isDesktop = window.matchMedia('(min-width: 990px)');

    function setVisible(on) {
      if (on && isDesktop.matches) on = false; // CSS hides it; skip the work
      bar.dataset.visible = on ? 'true' : 'false';
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      document.body.classList.toggle('pm-sticky-active', on);
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          // Show only once the real button has scrolled off the top.
          var e = entries[0];
          setVisible(!e.isIntersecting && e.boundingClientRect.top < 0);
        },
        { threshold: 0 }
      ).observe(target);
    }

    var stickyBtn = bar.querySelector('[data-pm-sticky-btn]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        var r = productInfo() || root;
        var real = submitButton(r);
        if (!real) return;

        if (real.hasAttribute('disabled')) {
          // No size chosen yet. Send them to the size row rather than doing
          // nothing, which is what a dead button feels like.
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var fs = sizeFieldset(r);
          var first = fs && fs.querySelector('input[type="radio"]');
          if (first) first.focus({ preventScroll: true });
          return;
        }
        real.click();
      });
    }
  }

  function init() {
    initSizeGate();
    initStickyBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();



/* PM PDP brief behaviours. */
(function(){'use strict';
  function shopParts(date,tz){var p=new Intl.DateTimeFormat('en-US',{timeZone:tz,year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'}).formatToParts(date),o={};p.forEach(function(x){if(x.type!=='literal')o[x.type]=Number(x.value)});return o}
  function shopDateParts(date,tz){var p=shopParts(date,tz);return {year:p.year,month:p.month,day:p.day,hour:p.hour,minute:p.minute,second:p.second,weekday:new Intl.DateTimeFormat('en-US',{timeZone:tz,weekday:'short'}).format(date)}}
  function addDays(d,n){var x=new Date(Date.UTC(d.year,d.month-1,d.day));x.setUTCDate(x.getUTCDate()+n);return {year:x.getUTCFullYear(),month:x.getUTCMonth()+1,day:x.getUTCDate()}}
  function iso(d){return String(d.year).padStart(4,'0')+'-'+String(d.month).padStart(2,'0')+'-'+String(d.day).padStart(2,'0')}
  function deliveryWindow(now,tz,cutoffHour,cutoffMinute,min,max,businessDays){var s=shopDateParts(now,tz), start={year:s.year,month:s.month,day:s.day};var before=s.hour<cutoffHour||(s.hour===cutoffHour&&s.minute<cutoffMinute);var weekday=s.weekday!=='Sat'&&s.weekday!=='Sun';if(!(before&&weekday)){do{start=addDays(start,1)}while(businessDays&&new Date(Date.UTC(start.year,start.month-1,start.day)).getUTCDay() % 6===0)}function add(n){var x=start,c=0;while(c<n){x=addDays(x,1);if(!businessDays||new Date(Date.UTC(x.year,x.month-1,x.day)).getUTCDay()%6!==0)c++}return x}return {start:add(min),end:add(max),startDate:start}}
  window.pmCalculateDeliveryWindow=deliveryWindow;
  function fmt(d){return new Intl.DateTimeFormat('en-US',{month:'short',day:'numeric',timeZone:'UTC'}).format(new Date(Date.UTC(d.year,d.month-1,d.day)))}
  function initGuides(){document.querySelectorAll('[data-pm-size-guide]').forEach(function(root){if(root.dataset.pmReady)return;root.dataset.pmReady='1';var b=root.querySelector('[data-pm-size-guide-trigger]'),dlg=root.querySelector('[data-pm-size-guide-dialog]'),last=null;root.querySelectorAll('[data-pm-unit]').forEach(function(btn){btn.addEventListener('click',function(){var unit=btn.dataset.pmUnit;root.querySelectorAll('[data-pm-unit]').forEach(function(x){x.setAttribute('aria-pressed',x===btn?'true':'false')});root.querySelectorAll('[data-unit]').forEach(function(x){x.style.display=x.dataset.unit===unit?'inline':'none'})})});if(!b||!dlg)return;function close(){if(dlg.open)dlg.close();document.body.classList.remove('pm-size-guide-open');if(last)last.focus()}b.addEventListener('click',function(){last=b;dlg.showModal();document.body.classList.add('pm-size-guide-open')});root.querySelector('[data-pm-size-guide-close]').addEventListener('click',close);dlg.addEventListener('click',function(e){if(e.target===dlg)close()});dlg.addEventListener('cancel',function(){close()});var source=root.querySelector('[data-pm-size-guide-source]');if(source&&!source.querySelector('table')){var n=root.querySelector('[data-pm-size-guide-empty]');if(n)n.hidden=false}})}
  function initCountdowns(){document.querySelectorAll('pm-countdown').forEach(function(el){if(el.dataset.pmReady)return;el.dataset.pmReady='1';var host=el.closest('[data-pm-support]'),tz=host&&host.dataset.shopTimezone||'UTC',h=Number(host&&host.dataset.cutoffHour||15),m=Number(host&&host.dataset.cutoffMinute||0),lastLabel=0,timer=null;function tick(){if(document.hidden)return;var now=new Date(),p=shopDateParts(now,tz),target=new Date(now.getTime()+1000),d=deliveryWindow(now,tz,h,m,5,12,true);var shopNowMs=Date.UTC(p.year,p.month-1,p.day,p.hour,p.minute,p.second),next=deliveryWindow(new Date(now.getTime()+86400000),tz,h,m,5,12,true);var diff=Math.max(0,((p.hour<h||(p.hour===h&&p.minute<m))?Date.UTC(p.year,p.month-1,p.day,h,m,0)-shopNowMs:Date.UTC(next.startDate.year,next.startDate.month-1,next.startDate.day,h,m,0)-shopNowMs));var hh=Math.floor(diff/3600000),mm=Math.floor(diff%3600000/60000),ss=Math.floor(diff%60000/1000);el.querySelector('[data-pm-hours]').textContent=String(hh).padStart(2,'0');el.querySelector('[data-pm-minutes]').textContent=String(mm).padStart(2,'0');el.querySelector('[data-pm-seconds]').textContent=String(ss).padStart(2,'0');if(Date.now()-lastLabel>60000){el.setAttribute('aria-label','Order within '+hh+' hours '+mm+' minutes');lastLabel=Date.now()}timer=setTimeout(tick,1000-(Date.now()%1000))}tick();document.addEventListener('visibilitychange',function(){if(!document.hidden){clearTimeout(timer);tick()}},{passive:true});el.addEventListener('DOMNodeRemoved',function(){clearTimeout(timer)})})}
  function initDelivery(){document.querySelectorAll('[data-pm-delivery]').forEach(function(el){var host=el.closest('[data-pm-support]');if(!host)return;var tz=host.dataset.shopTimezone||'UTC',w=deliveryWindow(new Date(),tz,Number(host.dataset.cutoffHour||15),Number(host.dataset.cutoffMinute||0),Number(host.dataset.shippingMin||5),Number(host.dataset.shippingMax||12),host.dataset.businessDays!=='false');var out=el.querySelector('[data-pm-delivery-text]');if(out)out.textContent='Estimated delivery '+fmt(w.start)+' - '+fmt(w.end)})}
  function init(){initGuides();initCountdowns();initDelivery()}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init);else init();document.addEventListener('shopify:section:load',init)
})();


/* Seven-day Autumn Sale cycle. It loops deterministically and is easy to disable from section settings. */
(function(){'use strict';function initAutumnSale(){document.querySelectorAll('pm-sale-timer').forEach(function(el){if(el.dataset.pmSaleReady)return;el.dataset.pmSaleReady='1';var days=Math.max(1,Number(el.dataset.cycleDays||7)),cycle=days*86400000,interval=null;function render(){var now=Date.now(),end=Math.ceil(now/cycle)*cycle,diff=Math.max(0,end-now),d=Math.floor(diff/86400000),h=Math.floor(diff%86400000/3600000),m=Math.floor(diff%3600000/60000),s=Math.floor(diff%60000/1000);el.querySelector('[data-sale-days]').textContent=String(d).padStart(2,'0');el.querySelector('[data-sale-hours]').textContent=String(h).padStart(2,'0');el.querySelector('[data-sale-minutes]').textContent=String(m).padStart(2,'0');el.querySelector('[data-sale-seconds]').textContent=String(s).padStart(2,'0');el.setAttribute('aria-label',(el.dataset.saleLabel||'Autumn Sale')+' ends in '+d+' days '+h+' hours '+m+' minutes '+s+' seconds')}render();interval=setInterval(render,1000);el.addEventListener('DOMNodeRemoved',function(){clearInterval(interval)})})}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',initAutumnSale);else initAutumnSale();document.addEventListener('shopify:section:load',initAutumnSale)})();
